/**
 * Created 28.10.2023
 * Pinia store for proposal data.
 */

import moment from 'moment-timezone';
import { defineStore } from 'pinia';
import { useSystemStore } from '@/Stores/System';
import {
  computed, ref,
} from 'vue';
import {
  getCurrentLanguage, uniqueId, getDateTimeInDestinationTimezone,
} from '@/utils';

const currentLanguage = ref(getCurrentLanguage());

export const useProposalStore = defineStore('proposal', () => {
  const systemStore = useSystemStore();

  /* *********************************************************************
   * Helper functions and variables
   * ****************************************************************** */

  /**
   * Fetch data from system store.
   * TODO: The following should be removed when all references are updated to use the system store.
   * TODO: EntityTypesIconMapping
   */
  const {
    entityTypesPluralMapping,
    entityTypesIconMapping,
    setAllMediaItems,
  } = systemStore;

  const printMode = computed(() => systemStore.printMode);
  const isEditable = ref(false);

  // Initial state for sections
  const initialSectionsState = {
    transport: {
      visible: true,
      heading: 'Transport',
      ingress: 'Her finner du informasjon om transporten på turen.',
    },
    lodgings: {
      visible: true,
      heading: 'Overnatting',
    },
    program: {
      visible: true,
      heading: 'Programoversikt',
      ingress: 'Her finner du en dag-for-dag oversikt over programmet for turen.'
              + '\n\n'
              + 'Klikk på bildene for å se mer info.',
    },
    activities: {
      visible: true,
      heading: 'Aktiviteter inkludert',
    },
    eateries: {
      visible: true,
      heading: 'Spisesteder på turen',
    },
    mediaGallery: {
      visible: true,
      heading: 'Bilder fra reisemålet',
    },
    prices: {
      visible: true,
      heading: 'Pristilbud',
      cta: 'Gi aksept på tilbudet her',
    },
  };

  /* *********************************************************************
   * Initial state setup
   * ****************************************************************** */

  // Function that creates and returns a new initial state object for proposal section data
  const createInitialSectionsState = () => (structuredClone(initialSectionsState));

  // TODO: Destination text from place should not be in proposal data.
  // Function that creates and returns a new initial state object for proposalData
  const createInitialProposalData = () => ({
    sideTitle: String() || null,
    brandSize: String() || null,
    transport: {
      ingress: String() || null,
      items: [], // This will become reactive when the parent object is made reactive
    },
    destination: {
      name: String() || null,
      heading: String() || null,
      description: String() || null,
      timezone: String() || null,
      image: {},
      media_crop: {},
      destination: {},
    },
    price_offer: {
      column_one: null,
      column_two: null,
      total: null,
      includes: null,
      additions: null,
      disclaimer: null,
    },
    sections: createInitialSectionsState(),
  });

  // Function that creates and returns a new initial state object for proposal
  const createInitialProposal = () => ({
    id: null,
    client: {
      id: null,
    },
    creator: {},
    currency: null,
    title: String() || null,
    subtitle: String() || null,
    data: createInitialProposalData(), // Use the function to create the nested object
    start_date: null,
    end_date: null,
    hash: null,
    accepted: false,
    accepted_at: null,
    accepted_data: {},
    accepted_version: {},
    accepted_sibling_id: null,
    brand: {
      logoUrl: String() || null,
      name: String() || null,
    },
    brand_id: null,
    terms: {},
    media: {},
    media_crop: {},
    media_id: null,
    notes: null,
    image: {},
    gallery: [],
    destinations: [], // This will become reactive when the parent object is made reactive
    pax: null,
    amount_pr_pax: null,
    project: {
      id: null,
      team: null,
      manager: null,
    },
    itinerary: [], // as served from database through inertia, not as used in frontend
    versions: [],
  });

  // Function that creates and returns a new initial state object
  const createInitialEntitiesState = () => ({
    lodgings: [],
    eateries: [],
    activities: [],
    venues: [],
    landmarks: [],
    others: [],
    globals: [],
  });

  // Function that creates and returns a new empty array
  const createEmptyArray = () => [];

  /* eslint-disable object-curly-newline */
  const createInitialNavState = () => ([
    { title: 'Intro', icon: 'GlobeEuropeAfricaIcon', component: 'ProposalIntroComponent', section: 'intro', current: false, visible: true },
    { title: 'Transport', icon: 'PaperAirplaneIcon', component: 'ProposalTransportComponent', section: 'transport', current: false, class: '-rotate-90', visible: true },
    { title: 'Overnatting', icon: 'HomeIcon', component: 'ProposalLodgingsComponent', section: 'lodgings', current: false, visible: true },
    { title: 'Program', icon: 'CalendarDaysIcon', component: 'ProposalProgramComponent', section: 'program', current: false, visible: true },
    { title: 'Priser', icon: 'ShoppingBagIcon', component: 'ProposalPricesComponent', section: 'prices', current: false, visible: true },
    { title: 'Skroll til bunn', icon: 'ChevronDoubleDownIcon', component: 'ProposalPricesComponent', section: 'noPrices', current: false, visible: false },
  ]);

  const createInitialUnsavedChanges = () => ({
    state: false,
    changes: {
      proposal: false,
      proposalDestinations: false,
      proposalBrandSize: false,
      navItems: false,
      itinerary: false,
    },
  });

  /* eslint-enable object-curly-newline */

  /* *********************************************************************
   * Set up reactive state
   * ****************************************************************** */

  const proposal = ref(structuredClone(createInitialProposal()));
  const proposalDestinations = ref(structuredClone(createEmptyArray()));
  const start = ref();
  const startAsStringInOriginalTimezone = ref();
  const startAsStringInCurrentTimezone = ref();
  const end = ref();
  const endAsStringInOriginalTimezone = ref();
  const endAsStringInCurrentTimezone = ref();
  const days = ref(structuredClone(createEmptyArray()));
  const itinerary = ref(structuredClone(createEmptyArray()));
  const entities = ref(structuredClone(createInitialEntitiesState()));
  const openEntityItinerarySettings = ref(0);
  const navItems = ref(structuredClone(createInitialNavState()));
  const initialUnsavedChanges = ref(structuredClone(createInitialUnsavedChanges()));

  const unsavedChanges = ref({
    state: computed(() => Object.values(unsavedChanges.value.changes).some((v) => v)),
    changes: initialUnsavedChanges.value.changes,
  });
  const editingTimer = ref(0);
  const saveTimer = ref(0);

  /* *********************************************************************
   * Reset helper functions
   * ****************************************************************** */

  const resetProposal = () => {
    proposal.value = structuredClone(createInitialProposal());
  };

  // Function to reset the entities to the initial state
  const resetEntities = () => {
    entities.value = structuredClone(createInitialEntitiesState());
  };

  const resetProposalDestinations = () => {
    proposalDestinations.value = structuredClone(createEmptyArray());
  };

  const resetDays = () => {
    // Replace the properties of days with a new initial state
    days.value = structuredClone(createEmptyArray());
  };

  const resetItinerary = () => {
    // Replace the properties of itinerary with a new initial state
    itinerary.value = structuredClone(createEmptyArray());
  };

  const resetOpenEntityItinerarySettings = () => {
    // Replace the properties of openEntityItinerary with a new initial state
    openEntityItinerarySettings.value = 0;
  };

  const resetNavItems = () => {
    itinerary.value = structuredClone(createInitialNavState());
  };

  /**
   * Function to reset proposal data to initial values
   */
  function $reset() {
    resetProposal();
    resetProposalDestinations();
    start.value = null;
    end.value = null;
    resetDays();
    unsavedChanges.value.changes = initialUnsavedChanges.value.changes;
    editingTimer.value = 0;
    saveTimer.value = 0;
    resetNavItems();
    resetItinerary();
    resetOpenEntityItinerarySettings();
    resetEntities();
    setAllMediaItems([]);
  }

  /* *********************************************************************
   * Update proposal data functions
   * ****************************************************************** */

  /**
   * Update itinerary with entities
   */
  function updateItinerary() {
    // Reset itinerary
    resetItinerary();

    days.value.forEach((day, index) => {
      if (!itinerary.value.some((item) => item.id === index + 1)) {
        itinerary.value.push({
          id: index + 1,
          active: isEditable.value === true ? true : (index === 0),
          date: day.date,
          title: day.title,
          list: [],
        });
      }
    });

    const createItineraryItem = (entity, entityType, entityIndex) => ({
      id: uniqueId(),
      time: getDateTimeInDestinationTimezone(entity.start, proposal.value.data.destination.timezone, 'HH:mm'),
      timezone: proposal.value.data.destination.timezone,
      title: entity.texts[currentLanguage.value].name,
      description: entity.texts[currentLanguage.value].description,
      proposalProgramText: entity.texts[currentLanguage.value].proposalProgramText,
      destinationId: entity.destination_id,
      visibleInProgram: entity.data.visibleInProgram === undefined ? true : entity.data.visibleInProgram,
      entity,
      entityType,
      entityIndex,
    });

    // Loop through all entities and add them to the itinerary
    Object.keys(entities.value).forEach((entityType) => {
      entities.value[entityType].forEach((entity, index) => {
        // Find the index of the itinerary date that matches the entity's start date
        let itineraryIndex = itinerary.value.findIndex(
          (item) => getDateTimeInDestinationTimezone(item.date, proposal.value.data.destination.timezone, 'YYYY-MM-DD')
          === getDateTimeInDestinationTimezone(entity.start, proposal.value.data.destination.timezone, 'YYYY-MM-DD')
        );
        // In case the entity's start date doesn't match any date in the itinerary, default to the first index
        if (itineraryIndex === -1) {
          itineraryIndex = 0;
        }
        // Check if the itinerary date exists, if not, create a new entry
        if (!itinerary.value[itineraryIndex]) {
          itinerary.value[itineraryIndex] = {
            date: getDateTimeInDestinationTimezone(entity.start, proposal.value.data.destination.timezone).toDate(), list: [],
          };
        }
        // Add the entity to the itinerary date
        itinerary.value[itineraryIndex].list.push(createItineraryItem(entity, entityType, index));
      });
    });

    const sortEntitiesInItinerary = (itineraryList) => {
      itineraryList.forEach((day) => {
        day.list.sort((a, b) => {
          const [hoursA, minutesA] = a.time.split(':').map(Number);
          const [hoursB, minutesB] = b.time.split(':').map(Number);

          return (hoursA * 60 + minutesA) - (hoursB * 60 + minutesB);
        });
      });
    };

    // Sort entities in itinerary
    sortEntitiesInItinerary(itinerary.value);
  }

  /**
   * Fetch entities from API, populate arrays and update itinerary
   * @param {boolean} isClone - Whether the proposal is a clone
   */
  function fetchEntities(isClone = false) {
    const sortEntities = (itineraryData, timezone) => itineraryData.slice().sort((a, b) => {
      const dateA = moment.tz(a.start, timezone);
      const dateB = moment.tz(b.start, timezone);

      return dateA.diff(dateB);
    });

    if (isClone) {
      resetEntities();
      const sortedEntities = sortEntities(proposal.value.itinerary, proposal.value.data.destination.timezone);
      // populate entities from database
      sortedEntities.forEach((entity) => {
        entities.value[entityTypesPluralMapping[entity.type]].push(entity);
      });
      updateItinerary();
    } else {
      axios.get(route('api.proposal.itinerary.index', {
        proposal: proposal.value.id,
      }))
        .then((response) => {
          resetEntities();
          const sortedEntities = sortEntities(response.data);
          // populate entities from database
          sortedEntities.forEach((entity) => {
            entities.value[entityTypesPluralMapping[entity.type]].push(entity);
          });
        }).finally(() => {
          updateItinerary();
        });
    }
  }

  /**
   * Update nav items based on proposal data
   * @param {object} sectionData - Proposal section data
   */
  function updateNavItems(sectionData) {
    if (sectionData) {
      navItems.value.forEach((item) => {
        if (item.section === 'noPrices') {
          item.visible = !sectionData.prices.visible;
        } else {
          item.visible = sectionData[item.section]?.visible ?? true;
        }
      });
    }
  }

  /**
   * Set brand size
   * @param {"small"|"medium"|"Large"} value - Brand size
   */
  function setBrandSize(value) {
    const validSizes = ['small', 'medium', 'large', 'xl'];
    if (!validSizes.includes(value)) {
      console.error(`Invalid brand size: ${value}. Must be one of ${validSizes.join(', ')}.`);

      return;
    }
    proposal.value.data.brandSize = value;
    unsavedChanges.value.changes.proposalBrandSize = true;
  }

  /**
   * Initialize proposal sections.
   * This function initializes the proposal sections with the initial state if they are null.
   */
  function initializeProposalSections() {
    Object.keys(initialSectionsState).forEach((sectionKey) => {
      const initialSection = initialSectionsState[sectionKey];
      const proposalSection = proposal.value.data.sections[sectionKey];

      // Check if the section is null, if so, initialize it
      Object.keys(initialSection).forEach((propKey) => {
        if (proposalSection[propKey] === null) {
          proposal.value.data.sections[sectionKey][propKey] = initialSection[propKey];
        }
      });
    });
  }

  /* *********************************************************************
   * Setup of proposal data
   * ****************************************************************** */

  /**
   * Set proposal data from data passed on initialisation
   * @param {object} data - Proposal data object
   * @param {boolean} isClone - Whether the proposal is a clone
   */
  function setProposal(data, isClone = false) {
    const databaseData = data;
    proposal.value = {
      ...proposal.value,
      ...databaseData,
      data: {
        ...proposal.value.data,
        ...databaseData.data,
        sections: {
          ...proposal.value.data.sections,
          ...(databaseData.data.sections ? databaseData.data.sections : {}),
        },
        price_offer: {
          ...proposal.value.data.price_offer,
          ...(databaseData.data.price_offer ? databaseData.data.price_offer : {}),
        },
      },
      accepted_version: {
        ...proposal.value.accepted_version,
        ...(databaseData.accepted_version ? databaseData.accepted_version : {}),
      },
      brand: {
        ...proposal.value.brand,
        ...(databaseData.brand ? databaseData.brand : {}),
      },
      terms: {
        ...proposal.value.terms,
        ...(databaseData.terms ? databaseData.terms : {}),
      },
      versions: [
        ...proposal.value.versions,
        ...(databaseData.versions ? databaseData.versions : []),
      ],
    };
    proposalDestinations.value = databaseData.destinations;

    // check of destination timezone is set
    if (databaseData.destinations[0]?.timezone) {
      // set destination timezone
      proposal.value.data.destination.timezone = databaseData.destinations[0].timezone;
      // Set start date as moment object in proposal data
      start.value = moment.tz(databaseData.start_date, proposal.value.data.destination.timezone);
      startAsStringInOriginalTimezone.value = start.value.format();
      startAsStringInCurrentTimezone.value = start.value.local(false).format();

      // Set end date as moment object in proposal data
      end.value = moment.tz(databaseData.end_date, proposal.value.data.destination.timezone);
      endAsStringInOriginalTimezone.value = end.value.format();
      endAsStringInCurrentTimezone.value = end.value.local(false).format();
    } else {
      console.warn('Missing destination timezone; cannot adjust for timezones');

      // Set start date as moment object in proposal data
      start.value = moment(databaseData.start_date);
      startAsStringInOriginalTimezone.value = moment(databaseData.start_date).format();
      startAsStringInCurrentTimezone.value = startAsStringInOriginalTimezone.value;

      // Set end date as moment object in proposal data
      end.value = moment(databaseData.end_date);
      endAsStringInOriginalTimezone.value = moment(databaseData.end_date).format();
      endAsStringInCurrentTimezone.value = endAsStringInOriginalTimezone.value;
    }

    // Check if proposal sections are initialized, if not, initialize them
    if (proposal.value.data.sections.transport.heading === null) {
      initializeProposalSections();
    }

    // Set days based on destination timezone
    const d = moment.tz(databaseData.end_date, proposal.value.data.destination.timezone).diff(moment.tz(databaseData.start_date, proposal.value.data.destination.timezone), 'days') + 1;
    for (let i = 0; i < d; i++) {
      const date = moment.tz(databaseData.start_date, proposal.value.data.destination.timezone).add(i, 'days');
      const title = date.format('dddd D. MMMM');
      const systemDate = date.format('YYYY-MM-DD');

      days.value.push({
        date: date.format(),
        title: title.charAt(0).toUpperCase() + title.slice(1),
        systemDate,
      });
    }
    // Fetch entities
    fetchEntities(isClone);
    // Update nav items
    updateNavItems(proposal.value.data.sections);
  }

  /**
   * Helper state variables to check if proposal data is ready
   */
  const proposalReady = computed(() => proposal.value !== null);
  const itineraryReady = computed(() => (
    itinerary.value !== null
  ));

  /* *********************************************************************
   * Proposal storage helper functions
   * ****************************************************************** */

  /**
   * Function to reset the unsaved changes timer
   * @param {boolean} keepUnsavedChangesState - Whether to keep unsaved changes state
   */
  function resetSaveTimer(keepUnsavedChangesState = false) {
    if (!keepUnsavedChangesState) {
      unsavedChanges.value.changes = initialUnsavedChanges.value.changes;
    }
    if (saveTimer.value !== 0) {
      clearTimeout(saveTimer.value);
      saveTimer.value = 0;
    }
  }

  /**
   * Set a timer to save unsaved changes
   * @param {Function} callback - The function to call when the timer is done
   * @param {number} delay - The delay in milliseconds
   */
  function setSaveTimer(callback, delay) {
    resetSaveTimer(true); // Clear any existing timer
    saveTimer.value = setTimeout(callback, delay);
  }

  /**
   * Function to reset the editing timer
   */
  function resetEditingTimer() {
    if (editingTimer.value !== 0) {
      clearTimeout(editingTimer.value);
      editingTimer.value = 0;
    }
  }

  /**
   * Set a timer to track editing changes
   * @param {Function} callback - The function to call when the timer is done
   * @param {number} delay - The delay in milliseconds
   * @returns {Promise} - A promise that resolves after the delay
   */
  function setEditingTimer(callback, delay) {
    resetEditingTimer(); // Clear any existing timer

    // Return a promise that resolves after the delay
    return new Promise((resolve) => {
      editingTimer.value = setTimeout(() => {
        callback();
        resolve(); // Resolve the promise after executing the callback
      }, delay);
    });
  }

  /**
   * Fetch proposal data from API
   * @param {string|number} id - Proposal id
   */
  function fetchProposal(id) {
    axios.get(route('api.proposal.show', { proposal: id }))
      .then((response) => {
        setProposal(response.data);
      });
  }

  return {
    proposal,
    proposalDestinations,
    start,
    startAsStringInOriginalTimezone,
    startAsStringInCurrentTimezone,
    end,
    endAsStringInOriginalTimezone,
    endAsStringInCurrentTimezone,
    days,
    itinerary,
    entities,
    navItems,
    unsavedChanges,
    setSaveTimer,
    resetSaveTimer,
    setEditingTimer,
    resetEditingTimer,
    proposalReady,
    itineraryReady,
    fetchProposal,
    setBrandSize,
    setProposal,
    fetchEntities,
    openEntityItinerarySettings,
    resetOpenEntityItinerarySettings,
    $reset,
    entityTypesPluralMapping,
    entityTypesIconMapping,
    initialSectionsState,
    printMode,
    isEditable,
  };
});
